import React, { useEffect, useState } from "react";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import DoNotDisturbAltRoundedIcon from "@mui/icons-material/DoNotDisturbAltRounded";
import GppMaybeRoundedIcon from "@mui/icons-material/GppMaybeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClipLoader from "react-spinners/ClipLoader";
import useQueryParams from "../../utils/query.params";
import { getRespondentSummary, isValidRespondentToken } from "../../services/ending.api";
import "./ending-pages.css";

const VARIANT_CONFIG = {
  success: {
    gradient: "linear-gradient(160deg, #f0fdf4 0%, #dcfce7 45%, #bbf7d0 100%)",
    accent: "#16a34a",
    accentSoft: "rgba(22,163,74,0.12)",
    ring: "rgba(34,197,94,0.35)",
    Icon: CheckCircleRoundedIcon,
    confetti: true,
    heading: "You're all set!",
  },
  terminate: {
    gradient: "linear-gradient(160deg, #fff7f2 0%, #ffe9de 55%, #ffdccb 100%)",
    accent: "#e2231a",
    accentSoft: "rgba(226,35,26,0.1)",
    ring: "rgba(226,35,26,0.3)",
    Icon: CancelRoundedIcon,
    confetti: false,
    heading: "Thanks for your time",
  },
  overquota: {
    gradient: "linear-gradient(160deg, #fff8ec 0%, #ffedc7 55%, #ffe19b 100%)",
    accent: "#b45309",
    accentSoft: "rgba(180,83,9,0.1)",
    ring: "rgba(180,83,9,0.3)",
    Icon: DoNotDisturbAltRoundedIcon,
    confetti: false,
    heading: "This survey is full",
  },
  "security-terminate": {
    gradient: "linear-gradient(160deg, #fdf2f8 0%, #fce7f3 55%, #fbcfe8 100%)",
    accent: "#9d174d",
    accentSoft: "rgba(157,23,77,0.1)",
    ring: "rgba(157,23,77,0.3)",
    Icon: GppMaybeRoundedIcon,
    confetti: false,
    heading: "We couldn't verify your session",
  },
};

const CONFETTI_PIECES = [
  { left: "6%", color: "#16a34a", delay: "0s", duration: "5.6s" },
  { left: "16%", color: "#f59e0b", delay: "0.6s", duration: "6.2s" },
  { left: "27%", color: "#0ea5e9", delay: "1.1s", duration: "5s" },
  { left: "39%", color: "#e2231a", delay: "0.3s", duration: "6.8s" },
  { left: "52%", color: "#16a34a", delay: "1.6s", duration: "5.8s" },
  { left: "64%", color: "#f59e0b", delay: "0.9s", duration: "6.4s" },
  { left: "77%", color: "#0ea5e9", delay: "0.2s", duration: "5.3s" },
  { left: "89%", color: "#e2231a", delay: "1.3s", duration: "6s" },
];

const CopyableCode = ({ code, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // clipboard unavailable in this browser context - ignore silently
    }
  };

  return (
    <div className="mt-5 w-full">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied" : "Copy code"}
        className="relative flex w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-10 py-2 font-mono transition hover:border-gray-400 hover:bg-gray-100"
      >
        <span className="font-mono text-2xl font-bold tracking-wide text-gray-800">{code}</span>
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
          {copied ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />}
        </span>
      </button>
    </div>
  );
};

const RespondentTerminalPage = ({ variant, message, children }) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.terminate;
  const Icon = config.Icon;
  const queryParams = useQueryParams();

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tokenError, setTokenError] = useState(null);

  useEffect(() => {
    // Summary API call only runs on the success page for now - other variants skip it entirely.
    if (variant !== "success") {
      setSummaryLoading(false);
      return undefined;
    }

    let isMounted = true;
    const token = queryParams.get("token");

    if (!isValidRespondentToken(token)) {
      setSummaryLoading(false);
      setTokenError("This link is missing a valid token, so your response details can't be shown.");
      return undefined;
    }

    setSummaryLoading(true);
    getRespondentSummary(token)
      .then((data) => {
        if (isMounted) setSummary(data);
      })
      .catch(() => {
        if (isMounted) setTokenError("We couldn't load your response details right now.");
      })
      .finally(() => {
        if (isMounted) setSummaryLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-10"
      style={{ background: config.gradient }}
    >
      {config.confetti &&
        CONFETTI_PIECES.map((piece, i) => (
          <span
            key={i}
            className="rf-ending-confetti"
            style={{
              left: piece.left,
              background: piece.color,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
            }}
          />
        ))}

      <div className="rf-ending-rise relative z-10 w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
        <div className="flex justify-center">
          <div
            className="rf-ending-glow flex h-24 w-24 items-center justify-center rounded-full"
            style={{ background: config.accentSoft, "--rf-glow-color": config.ring }}
          >
            <Icon sx={{ fontSize: 56, color: config.accent }} />
          </div>
        </div>

        <h1 className="rf-ending-rise-delay-1 mt-6 text-2xl font-bold text-gray-900">{config.heading}</h1>

        <div
          className="rf-ending-rise-delay-1 mt-4 rounded-xl border-l-4 px-4 py-3 text-left text-sm italic text-gray-600"
          style={{ borderColor: config.accent, background: config.accentSoft }}
        >
          {message}
        </div>


        <div className="rf-ending-rise-delay-2 mt-5">
          {summaryLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-2 text-sm text-gray-400">
              <ClipLoader color={config.accent} loading size={26} />
              Loading your response details...
            </div>
          ) : tokenError ? (
            <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-4 py-3 text-left text-xs text-gray-500">
              <InfoOutlinedIcon sx={{ fontSize: 16 }} className="mt-0.5 shrink-0" />
              <span>{tokenError}</span>
            </div>
          ) : variant === "success" && summary && summary.supplierSuccessCode ? (
            <CopyableCode code={summary.supplierSuccessCode} label="Your confirmation code" />
          ) : null}
        </div>

        {children}

        <p className="rf-ending-rise-delay-2 mt-6 text-xs text-gray-400">You may now safely close this tab.</p>
      </div>
    </div>
  );
};

export default RespondentTerminalPage;
export { CopyableCode };
